import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, Printer } from 'lucide-react'
import { getCogm } from '../../api/production'
import ErrorBanner from '../../components/ErrorBanner'
import HelpTip from '../../components/HelpTip'
import Spinner from '../../components/Spinner'
import { fmtDay, money } from '../../utils/format'
import { useSession } from '../../session'
import { PERIODS, PeriodPicker, StatementHead, StatementTable, exportRowsCsv, glHref, periodLabel, useCompanyName, usePeriodParams } from '../accounting/reportsShared'
import InventoryTabs from './InventoryTabs'
import { qty } from './inventoryShared'
import { printPage, useAutoPrint } from '../../utils/print'

const COGM_HELP = 'What the goods you finished making in the period cost: the materials, labour and overhead put into production, adjusted for the work still in progress at the start and end. It’s the manufacturer’s version of purchases in cost of goods sold.'

// Schedule of cost of goods manufactured (GAAP): raw materials used, direct
// labour and overhead, the change in work in progress, then finished goods.
// Every figure comes from the books, so it ties to the balance sheet.
function cogmRows(d, { books } = {}) {
  const gl = (code) => (books && code ? glHref(code, d.start, d.end) : undefined)
  const a = d.accounts
  const r = []
  const raw = d.raw_materials
  if (raw && [raw.begin, raw.purchases, raw.used, raw.other, raw.end].some((v) => Math.abs(v) >= 0.005)) {
    r.push({ kind: 'heading', label: 'Raw materials', help: 'Materials kept in stock for making things. What went into production is what’s left after purchases, other changes (sales, counts) and what’s still on the shelf.' })
    r.push({ kind: 'line', label: 'Raw materials at the start', amount: raw.begin, link: gl(a.raw_materials) })
    r.push({ kind: 'line', label: 'Add: purchases and other additions', amount: raw.purchases })
    r.push({ kind: 'subtotal', label: 'Raw materials available', amount: raw.begin + raw.purchases })
    if (Math.abs(raw.other) >= 0.005) r.push({ kind: 'line', label: 'Less: sold, counted or adjusted', amount: -raw.other })
    r.push({ kind: 'line', label: 'Less: raw materials at the end', amount: -raw.end, link: gl(a.raw_materials) })
    r.push({ kind: 'subtotal', label: 'Raw materials put into production', amount: raw.used })
  }
  r.push({ kind: 'heading', label: 'Manufacturing costs' })
  r.push({ kind: 'line', label: 'Materials from stock', amount: d.materials.from_stock, help: 'Taken from stock for runs, at their FIFO cost, less what was put back.' })
  r.push({ kind: 'line', label: 'Materials bought for batches', amount: d.materials.bought, help: 'Bought for a run: recorded as a raw materials purchase, then moved straight into the run. Less what was put back.' })
  r.push({ kind: 'subtotal', label: 'Direct materials used', amount: d.materials.total })
  r.push({ kind: 'line', label: 'Direct labour', amount: d.labour, help: 'The makers’ pay: recorded in Factory labour, then moved into runs.' })
  r.push({ kind: 'line', label: 'Manufacturing overhead', amount: d.overhead, help: 'Indirect costs of making things, such as rent of the workshop, power and machine upkeep: recorded in Manufacturing overhead, then assigned to runs. What isn’t assigned to a run goes to cost of goods sold at month-end.' })
  if (Math.abs(d.other_costs) >= 0.005) r.push({ kind: 'line', label: 'Other production costs', amount: d.other_costs })
  r.push({ kind: 'subtotal', label: 'Total manufacturing costs', amount: d.added })
  r.push({ kind: 'line', label: 'Add: work in progress at the start', amount: d.wip_begin, link: gl(a.work_in_progress) })
  if (Math.abs(d.other_entries) >= 0.005) r.push({ kind: 'line', label: 'Other entries to work in progress', amount: d.other_entries, help: 'Journal entries to work in progress made outside Production.' })
  r.push({ kind: 'subtotal', label: 'Total cost of work in progress', amount: d.to_account_for })
  if (Math.abs(d.waste) >= 0.005) r.push({ kind: 'line', label: 'Less: unusual waste (production losses)', amount: -d.waste, help: 'Waste recorded as a loss when a run finished. Normal waste stays in the cost of the good units.' })
  if (Math.abs(d.cancelled) >= 0.005) r.push({ kind: 'line', label: 'Less: cancelled runs', amount: -d.cancelled, help: 'Taken out of work in progress when a run was cancelled: materials back to stock, the rest to production losses.' })
  r.push({ kind: 'line', label: 'Less: work in progress at the end', amount: -d.wip_end, link: gl(a.work_in_progress) })
  r.push({ kind: 'total', label: 'Cost of goods manufactured', amount: d.cogm, help: COGM_HELP })
  if (Math.abs(d.corrections) >= 0.005) r.push({ kind: 'note', label: `Includes ${money(d.corrections)} of cost corrections to runs that had already finished.` })
  const f = d.finished_goods
  r.push({ kind: 'heading', label: 'Finished goods' })
  r.push({ kind: 'line', label: 'Finished goods at the start', amount: f.begin, link: gl(a.finished_goods) })
  r.push({ kind: 'line', label: 'Add: cost of goods manufactured', amount: f.cogm })
  r.push({ kind: 'subtotal', label: 'Finished goods available', amount: f.available })
  r.push({ kind: 'line', label: 'Less: finished goods at the end', amount: -f.end, link: gl(a.finished_goods) })
  r.push({ kind: 'total', label: 'Finished goods sold or used', amount: f.out, help: 'The cost of what you made that left stock: sold (cost of goods sold), used in other runs, counted or adjusted.' })
  return r
}

export default function ProductionCogm() {
  const { has } = useSession()
  const books = has('accounting')
  const company = useCompanyName()
  const pp = usePeriodParams('this_month')
  const { period, start, end } = pp
  const [data, setData] = useState(null)
  useAutoPrint(!!data)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!start || !end) return
    setError('')
    setData(null)
    getCogm({ start, end }).then(setData).catch((e) => setError(e.message || 'Could not load the report.'))
  }, [start, end])

  const rows = useMemo(() => (data ? cogmRows(data, { books }) : []), [data, books])
  const label = period === 'custom' ? 'This period' : PERIODS.find((p) => p.id === period)?.label

  return (
    <div className="ci-page fs-page">
      <InventoryTabs />
      <div className="ci-header-row fs-noprint">
        <div className="ci-title-block">
          <div>
            <h1>Cost of goods manufactured <HelpTip text={COGM_HELP} /></h1>
            <div className="ci-title-meta">What you made in a period cost, from materials to finished goods.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => exportRowsCsv(rows, { name: 'cost_of_goods_manufactured', currentLabel: `${start} to ${end}` })}><Download size={15} />Export</button>
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => printPage()}><Printer size={15} />Print</button>
        </div>
      </div>

      <div className="ci-toolbar fs-noprint">
        <PeriodPicker {...pp} />
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? (!error && <Spinner label="Preparing the report…" />) : (
        <>
          <section className="fs-card">
            <StatementHead company={company} title="Schedule of cost of goods manufactured" sub={`For the period ${periodLabel(data.start, data.end)}`} />
            <StatementTable key={`${start}-${end}`} rows={rows} currentLabel={label} glRange={[start, end]} />
            {Math.abs(data.difference) >= 0.005 && (
              <p className="fs-foot">Work in progress is out by {money(data.difference)}: an entry to it doesn’t balance. Check the general ledger for work in progress.</p>
            )}
          </section>

          <section className="fs-card">
            <h2 className="fs-section-title">Runs finished in the period</h2>
            <div className="ci-table-scroll">
              <table className="ci-table fit-sm">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Made</th>
                    <th className="num">Units</th>
                    <th className="num hide-sm">Cost</th>
                    <th className="num">Per unit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.length === 0 ? (
                    <tr><td colSpan="5"><div className="ci-empty">No runs finished in this period.</div></td></tr>
                  ) : data.runs.map((r) => (
                    <tr key={r.run_id}>
                      <td><Link className="ci-je-link" to={`/inventory/production/${r.run_id}`}>{r.label}</Link><span className="cell-sub">{fmtDay(r.finished_on)}</span></td>
                      <td>{r.product_name}</td>
                      <td className="num">{qty(r.made_qty)}{r.scrap_qty > 0 && <span className="cell-sub">{qty(r.scrap_qty)} wasted</span>}</td>
                      <td className="num hide-sm">{money(r.total_cost)}</td>
                      <td className="num">{money(r.unit_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
